
/**
 * Shared aesthetics for the Drafting Table theme.
 * Core component styles for Sketchy borders.
 */
export const coreStyles = `
  .sketch-base { position: relative; }
  .sketch-base::before, .sketch-base::after {
    content: ''; position: absolute; pointer-events: none;
    border-radius: var(--radius-sm, 2px); z-index: 1; opacity: 0.6;
    transition: all 0.2s ease;
  }
  .sketch-base::before {
    border: 2px solid var(--border-sketch, rgba(245, 158, 11, 0.5));
    filter: url(#sketchy-strong); opacity: 0.6;
  }
  .sketch-base::after {
    border: 1.5px solid var(--border-sketch);
    filter: url(#sketchy-strong); opacity: 0.4;
  }

  .sketch-simple { position: relative; }
  .sketch-simple::before, .sketch-simple::after {
    content: ''; position: absolute; z-index: 1;
    border-radius: var(--radius-sm); transition: all 0.2s;
  }
  .sketch-simple::before {
    border: 2px solid var(--border-sketch);
    filter: url(#sketchy);
  }
  .sketch-simple::after {
    border: 1.2px solid var(--border-sketch);
    filter: url(#sketchy); opacity: 0.5;
  }

  .shadow-industrial { box-shadow: 4px 4px 0px rgba(0, 0, 0, 0.4); }
  .interactive { cursor: pointer; transition: transform 0.2s, box-shadow 0.2s; }
  .interactive:hover { transform: translate(-1px, -1px); }
  .interactive:active { transform: translate(1px, 1px); }

  .drafting-box {
    position: relative;
    background: rgba(15, 15, 15, 0.8);
    backdrop-filter: blur(8px);
    border: 1.5px solid var(--border-sketch);
    border-radius: var(--radius-sm);
  }

  .drafting-box::before {
    content: '';
    position: absolute;
    top: -4px; left: -4px; right: -4px; bottom: -4px;
    border: 1px solid var(--border-sketch);
    border-radius: var(--radius-sm);
    filter: url(#sketchy);
    opacity: 0.3;
    pointer-events: none;
  }
`;

/**
 * Layout-specific sketchy borders (single sides).
 */
export const layoutStyles = `
  .sketch-border-right { position: relative; }
  .sketch-border-right::before, .sketch-border-right::after {
    content: ''; position: absolute; top: 0; bottom: 0; width: 2px; right: 0; pointer-events: none;
  }
  .sketch-border-right::before { background: var(--border-sketch); filter: url(#sketchy-strong); }
  .sketch-border-right::after { right: -2px; background: var(--border-sketch); filter: url(#sketchy); opacity: 0.3; }

  .sketch-border-bottom { position: relative; }
  .sketch-border-bottom::before, .sketch-border-bottom::after {
    content: ''; position: absolute; bottom: 0; left: 0; right: 0; height: 2px; pointer-events: none;
  }
  .sketch-border-bottom::before { background: var(--border-sketch); filter: url(#sketchy); }
  .sketch-border-bottom::after { bottom: -2px; height: 1.5px; background: var(--border-sketch); filter: url(#sketchy-strong); opacity: 0.3; }

  .grid-paper {
    background-color: var(--bg-main, #0a0a0a);
    background-image: 
      linear-gradient(rgba(245, 158, 11, 0.1) 1px, transparent 1px),
      linear-gradient(90deg, rgba(245, 158, 11, 0.1) 1px, transparent 1px),
      linear-gradient(rgba(245, 158, 11, 0.04) 1px, transparent 1px),
      linear-gradient(90deg, rgba(245, 158, 11, 0.04) 1px, transparent 1px);
    background-size: 150px 150px, 150px 150px, 30px 30px, 30px 30px;
  }
`;

export const scrollbarStyles = `
  ::-webkit-scrollbar {
    width: 12px;
    height: 12px;
  }

  ::-webkit-scrollbar-track {
    background: rgba(0, 0, 0, 0.2);
  }

  ::-webkit-scrollbar-thumb {
    background: rgba(245, 158, 11, 0.2);
    border-radius: 20px;
    border: 3px solid transparent;
    background-clip: content-box;
  }

  ::-webkit-scrollbar-thumb:hover {
    background: rgba(245, 158, 11, 0.5);
    background-clip: content-box;
  }
`;

export const draftingStyles = coreStyles + layoutStyles + scrollbarStyles;

export const injectAesthetic = (seed?: number | string) => {
  const s = seed !== undefined ? (typeof seed === 'number' ? seed : parseInt(seed.toString().split('').map(c => c.charCodeAt(0)).join('').slice(0, 5))) : Math.floor(Math.random() * 10000);

  // Seed-based stable random helper
  const rnd = (min: number, max: number, offset = 0) => {
    const val = (Math.sin(s + offset) + 1) / 2;
    return (val * (max - min) + min).toFixed(2);
  };

  const idSuffix = `-${s}`;
  const strongId = `filt-strong${idSuffix}`;
  const simpleId = `filt-simple${idSuffix}`;

  const freq1 = (0.02 + (parseFloat(rnd(-0.002, 0.002, 10)))).toFixed(4);
  const freq2 = (0.015 + (parseFloat(rnd(-0.001, 0.001, 20)))).toFixed(4);

  const b1_t = rnd(-3, -7, 1);
  const b1_l = rnd(-4, -8, 2);
  const b1_r = rnd(0, -4, 3);
  const b1_b = rnd(0, -6, 4);
  const b1_rot = rnd(-0.2, -1.2, 5);
  const b1_skew = rnd(-0.2, -0.8, 6);

  const a1_t = rnd(-5, -10, 7);
  const a1_l = rnd(-1, -5, 8);
  const a1_r = rnd(-5, -10, 9);
  const a1_b = rnd(-2, -6, 10);
  const a1_rot = rnd(0.4, 1.4, 11);
  const a1_skew = rnd(-0.1, -0.6, 12);

  const b2_t = rnd(-3, -5, 13);
  const b2_l = rnd(-3, -6, 14);
  const b2_r = rnd(-1, -4, 15);
  const b2_b = rnd(-1, -4, 16);
  const b2_rot = rnd(-0.3, -0.8, 17);

  const a2_t = rnd(-4, -6, 18);
  const a2_l = rnd(-1, -4, 19);
  const a2_r = rnd(-4, -7, 20);
  const a2_b = rnd(-2, -5, 21);
  const a2_rot = rnd(0.3, 0.9, 22);

  const processedStyles = coreStyles
    .replaceAll('url(#sketchy-strong)', `url(#${strongId})`)
    .replaceAll('url(#sketchy)', `url(#${simpleId})`);

  return `
  <style>
    ${processedStyles}
    ${scrollbarStyles}
    .sketch-base::before {
      top: ${b1_t}px; left: ${b1_l}px; right: ${b1_r}px; bottom: ${b1_b}px;
      transform: rotate(${b1_rot}deg) skewX(${b1_skew}deg);
    }
    .sketch-base::after {
      top: ${a1_t}px; left: ${a1_l}px; right: ${a1_r}px; bottom: ${a1_b}px;
      transform: rotate(${a1_rot}deg) skewY(${a1_skew}deg);
    }
    .sketch-simple::before {
      top: ${b2_t}px; left: ${b2_l}px; right: ${b2_r}px; bottom: ${b2_b}px;
      transform: rotate(${b2_rot}deg);
    }
    .sketch-simple::after {
      top: ${a2_t}px; left: ${a2_l}px; right: ${a2_r}px; bottom: ${a2_b}px;
      transform: rotate(${a2_rot}deg);
    }
  </style>
  <svg style="position:absolute;width:0;height:0;overflow:hidden;" aria-hidden="true">
    <defs>
      <filter id="${simpleId}" x="-10%" y="-10%" width="120%" height="120%">
        <feTurbulence type="turbulence" baseFrequency="${freq1}" numOctaves="2" seed="${s}" result="noise" />
        <feDisplacementMap in="SourceGraphic" in2="noise" scale="1.5" xChannelSelector="R" yChannelSelector="G" />
      </filter>
      <filter id="${strongId}" x="-20%" y="-20%" width="140%" height="140%">
        <feTurbulence type="turbulence" baseFrequency="${freq2}" numOctaves="3" seed="${s}" result="noise" />
        <feDisplacementMap in="SourceGraphic" in2="noise" scale="3" xChannelSelector="R" yChannelSelector="G" />
      </filter>
      <!-- Global Aliases -->
      <filter id="sketchy" x="-10%" y="-10%" width="120%" height="120%">
        <feTurbulence type="turbulence" baseFrequency="${freq1}" numOctaves="2" seed="${s}" result="noise" />
        <feDisplacementMap in="SourceGraphic" in2="noise" scale="1.5" xChannelSelector="R" yChannelSelector="G" />
      </filter>
      <filter id="sketchy-strong" x="-20%" y="-20%" width="140%" height="140%">
        <feTurbulence type="turbulence" baseFrequency="${freq2}" numOctaves="3" seed="${s}" result="noise" />
        <feDisplacementMap in="SourceGraphic" in2="noise" scale="3" xChannelSelector="R" yChannelSelector="G" />
      </filter>
    </defs>
  </svg>
`;
};

export const injectLayoutAesthetic = (seed?: number | string) => {
  const base = injectAesthetic(seed);
  return base.replace('</style>', `${layoutStyles}</style>`);
};

export const theme = {
  accent: '#f59e0b',
  bgCard: '#151515',
  borderSketch: 'rgba(245, 158, 11, 0.5)',
  shadowIndustrial: '4px 4px 0px rgba(0, 0, 0, 0.4)',
};
